import { useEffect, useState } from "react";
import "@/i18n";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import PageLayout from "@/components/PageLayout";
import CookieBanner from "@/components/CookieBanner";
import Home from "@/pages/Home";
import Privacy from "@/pages/Privacy";
import Cookies from "@/pages/Cookies";
import Terms from "@/pages/Terms";
import ThankYou from "@/pages/ThankYou";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "@/i18n";
import { applySavedConsentOnLoad } from "@/consent/consent";

function LangGuard({ children }) {
  const { lang } = useParams();
  if (!SUPPORTED_LANGS.includes(lang)) {
    return <Navigate to={`/${DEFAULT_LANG}`} replace />;
  }
  return children({ lang });
}

function HashScroller() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  }, [location.hash, location.pathname]);
  return null;
}

function App() {
  const [cookieOpen, setCookieOpen] = useState(false);
  const [forceOpenModal, setForceOpenModal] = useState(false);

  useEffect(() => {
    applySavedConsentOnLoad();
  }, []);

  const reopenCookies = () => {
    setForceOpenModal(true);
  };

  const withLayout = (Component, extraProps = {}) => (
    <LangGuard>
      {({ lang }) => (
        <PageLayout lang={lang} onReopenCookies={reopenCookies}>
          <Component lang={lang} onReopenCookies={reopenCookies} {...extraProps} />
        </PageLayout>
      )}
    </LangGuard>
  );

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <HashScroller />
        <Routes>
          <Route path="/" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
          <Route path="/thank_you" element={<ThankYou />} />
          <Route path="/:lang" element={withLayout(Home)} />
          <Route path="/:lang/privacy" element={withLayout(Privacy)} />
          <Route path="/:lang/cookies" element={withLayout(Cookies)} />
          <Route path="/:lang/terms" element={withLayout(Terms)} />
          <Route path="*" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
        </Routes>

        <CookieBanner
          open={cookieOpen}
          setOpen={setCookieOpen}
          forceOpenModal={forceOpenModal}
          setForceOpenModal={setForceOpenModal}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
